"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { fetchLivescoreBoard } from "@/features/live-data/livescoreClient";
import { seedMatches, type LivescoreBoardSeed } from "@/features/live-data/livescoreParams";
import type { LivescoreBoard } from "@/views/livescore/utils/livescore";
import { LIVESCORE_REFRESH_INTERVAL_MS } from "@/config/livescore";
import type { LocaleCode } from "@/lib/labels/types";
interface UseAllSportsLivescoreMatchesOptions {
  date: string;
  locale: LocaleCode;
  /** Server-rendered board; used in place of the first fetch when it answers the initial query. */
  initialBoard?: LivescoreBoardSeed;
}
export interface AllSportsLivescoreData extends LivescoreBoard {
  loading: boolean;
}
const EMPTY_BOARD: LivescoreBoard = {
  groups: [],
  entities: {}
};

/**
 * Fetches the multi-sport livescore board once per mount, then refreshes it
 * every 30 seconds via GET /api/live/livescore — a single call that returns
 * groups from every sport when no `sport` filter is set.
 */
export function useAllSportsLivescoreMatches({
  date,
  locale,
  initialBoard
}: UseAllSportsLivescoreMatchesOptions): AllSportsLivescoreData {
  const seeded = seedMatches(initialBoard, {
    date,
    locale,
    filter: "all"
  });
  const [board, setBoard] = useState<LivescoreBoard>(seeded ? initialBoard.board : EMPTY_BOARD);
  const [loading, setLoading] = useState(!seeded);
  const seedConsumedRef = useRef(false);
  const loadBoard = useCallback(async (isRefresh = false) => {
    if (!isRefresh) setLoading(true);
    try {
      // null = request failed: keep the last good board on a refresh, show empty on first load.
      // An empty board is a real result (no matches for this filter) and replaces the state.
      const result = await fetchLivescoreBoard({
        date,
        locale,
        filter: "all"
      });
      if (result) setBoard(result);else if (!isRefresh) setBoard(EMPTY_BOARD);
    } finally {
      setLoading(false);
    }
  }, [date, locale]);
  useEffect(() => {
    if (seeded && !seedConsumedRef.current) {
      seedConsumedRef.current = true;
      return;
    }
    loadBoard(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadBoard]);
  useEffect(() => {
    const interval = setInterval(() => loadBoard(true), LIVESCORE_REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [loadBoard]);
  return {
    ...board,
    loading
  };
}